import React, { useState, useEffect, useRef, useCallback } from 'react';
import Map, { Source, Layer, Marker, Popup, NavigationControl } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster', source: 'osm', minzoom: 0, maxzoom: 19 }],
};

const isMoving = (speed) => (speed ?? 0) > 0.5;

const DOT_MOVING  = '#22c55e';
const DOT_STOPPED = '#ef4444';

const popupStyle = {
  wrapper: {
    fontFamily: 'monospace',
    fontSize: 11,
    lineHeight: 1.6,
    padding: '4px 2px',
    minWidth: 140,
  },
  row: { display: 'flex', alignItems: 'center', gap: 5 },
  label: { color: '#6b7280', minWidth: 60 },
  value: { fontWeight: 700, color: '#111827' },
  badge: (moving) => ({
    display: 'inline-block',
    padding: '1px 6px',
    borderRadius: 9999,
    background: moving ? '#dcfce7' : '#fee2e2',
    color: moving ? '#15803d' : '#b91c1c',
    fontWeight: 700,
    fontSize: 10,
  }),
};

const TrackingMap = ({ coordinates, tamanio, animated = false, currentPosition = null }) => {
  const [displayedPath, setDisplayedPath] = useState([]);
  const [animDone, setAnimDone] = useState(false);
  const [viewState, setViewState] = useState({ longitude: -99.1332, latitude: 19.4326, zoom: 5 });
  const [popup, setPopup] = useState(null);
  const intervalRef = useRef(null);
  const mapRef = useRef(null);

  const fitBounds = useCallback((coords) => {
    if (!mapRef.current || !coords || coords.length === 0) return;
    const lngs = coords.map(c => c.lng);
    const lats = coords.map(c => c.lat);
    const sw = [Math.min(...lngs), Math.min(...lats)];
    const ne = [Math.max(...lngs), Math.max(...lats)];
    if (sw[0] === ne[0] && sw[1] === ne[1]) {
      mapRef.current.flyTo({ center: [sw[0], sw[1]], zoom: 14, duration: 600 });
    } else {
      mapRef.current.fitBounds([sw, ne], { padding: 48, duration: 800 });
    }
  }, []);

  useEffect(() => {
    if (!coordinates || coordinates.length === 0) return;
    clearInterval(intervalRef.current);
    setPopup(null);

    if (animated) {
      setDisplayedPath([coordinates[0]]);
      setAnimDone(false);
      const total = coordinates.length;
      const delay = Math.max(80, Math.min(450, Math.round(2800 / total)));
      let idx = 1;
      intervalRef.current = setInterval(() => {
        idx += 1;
        setDisplayedPath(coordinates.slice(0, idx));
        if (idx >= total) {
          clearInterval(intervalRef.current);
          setAnimDone(true);
        }
      }, delay);
    } else {
      setDisplayedPath(coordinates);
      setAnimDone(true);
    }

    return () => clearInterval(intervalRef.current);
  }, [coordinates, animated]);

  const onMapLoad = useCallback(() => fitBounds(coordinates), [coordinates, fitBounds]);

  const containerStyle = tamanio || { width: '100%', height: '280px' };

  if (!coordinates || coordinates.length === 0) {
    return (
      <div style={{
        ...containerStyle, background: '#f3f4f6', display: 'flex',
        alignItems: 'center', justifyContent: 'center', borderRadius: 8, color: '#9ca3af',
      }}>
        Sin coordenadas
      </div>
    );
  }

  const lineGeoJSON = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: displayedPath.map(c => [c.lng, c.lat]),
    },
  };

  const tail  = displayedPath[displayedPath.length - 1];
  const start = coordinates[0];
  const end   = coordinates[coordinates.length - 1];

  /* Intermediate points (excluding first and last) shown only when animation done */
  const intermediates = animDone && coordinates.length > 2
    ? coordinates.slice(1, -1)
    : [];

  const formatUtc = (utc) => {
    if (!utc) return null;
    const t = utc.split(' ');
    return t[1] ?? utc;
  };

  return (
    <Map
      ref={mapRef}
      {...viewState}
      onMove={e => setViewState(e.viewState)}
      style={containerStyle}
      mapStyle={OSM_STYLE}
      onLoad={onMapLoad}
      onClick={() => setPopup(null)}
    >
      <NavigationControl position="top-right" />

      {/* Route line */}
      {displayedPath.length > 1 && (
        <Source id="route" type="geojson" data={lineGeoJSON}>
          <Layer
            id="route-casing"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': '#ffffff', 'line-width': 5, 'line-opacity': 0.7 }}
          />
          <Layer
            id="route-line"
            type="line"
            layout={{ 'line-join': 'round', 'line-cap': 'round' }}
            paint={{ 'line-color': '#3b82f6', 'line-width': 3 }}
          />
        </Source>
      )}

      {/* Intermediate dots — green=moving, red=stopped */}
      {intermediates.map((pt, i) => {
        const moving = isMoving(pt.speed);
        return (
          <Marker
            key={`mid-${i}`}
            longitude={pt.lng}
            latitude={pt.lat}
            anchor="center"
            onClick={e => { e.originalEvent.stopPropagation(); setPopup(pt); }}
          >
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: moving ? DOT_MOVING : DOT_STOPPED,
              border: '1.5px solid white',
              boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
              cursor: 'pointer',
            }} />
          </Marker>
        );
      })}

      {/* Start marker — always visible */}
      <Marker
        longitude={start.lng}
        latitude={start.lat}
        anchor="center"
        onClick={e => { e.originalEvent.stopPropagation(); setPopup(start); }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: '50%', background: '#10b981',
          border: '2.5px solid white', display: 'flex', alignItems: 'center',
          justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13,
          boxShadow: '0 2px 8px rgba(0,0,0,0.35)', cursor: 'pointer',
        }}>A</div>
      </Marker>

      {/* End marker — visible only when animation completes */}
      {animDone && coordinates.length > 1 && (
        <Marker
          longitude={end.lng}
          latitude={end.lat}
          anchor="center"
          onClick={e => { e.originalEvent.stopPropagation(); setPopup(end); }}
        >
          <div style={{
            width: 30, height: 30, borderRadius: '50%', background: '#ef4444',
            border: '2.5px solid white', display: 'flex', alignItems: 'center',
            justifyContent: 'center', color: 'white', fontWeight: 700, fontSize: 13,
            boxShadow: '0 2px 8px rgba(0,0,0,0.35)', cursor: 'pointer',
          }}>B</div>
        </Marker>
      )}

      {/* Moving vehicle marker at tail during animation */}
      {!animDone && tail && displayedPath.length > 1 && (
        <Marker longitude={tail.lng} latitude={tail.lat} anchor="center">
          <div style={{
            width: 22, height: 22, borderRadius: '50%',
            background: isMoving(tail.speed) ? DOT_MOVING : DOT_STOPPED,
            border: '2.5px solid white', display: 'flex', alignItems: 'center',
            justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
          }}>
            <div style={{ width: 7, height: 7, borderRadius: '50%', background: 'white' }} />
          </div>
        </Marker>
      )}

      {/* Current-position live marker */}
      {(() => {
        if (!currentPosition) return null;
        const cpLat = parseFloat(currentPosition.latitud);
        const cpLng = parseFloat(currentPosition.longitud);
        if (isNaN(cpLat) || isNaN(cpLng)) return null;
        const cpMoving = isMoving(currentPosition.velocidad_kmh);
        const cpColor = cpMoving ? DOT_MOVING : DOT_STOPPED;
        return (
          <Marker longitude={cpLng} latitude={cpLat} anchor="center">
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                position: 'absolute', width: 44, height: 44, borderRadius: '50%',
                background: cpColor, opacity: 0.22,
                animation: 'tracking-pulse 1.8s ease-out infinite',
              }} />
              <div style={{
                position: 'absolute', width: 30, height: 30, borderRadius: '50%',
                background: cpColor, opacity: 0.16,
                animation: 'tracking-pulse 1.8s ease-out infinite',
                animationDelay: '0.45s',
              }} />
              <div style={{
                width: 18, height: 18, borderRadius: '50%',
                background: cpColor, border: '3px solid white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
                position: 'relative', zIndex: 2,
              }} />
            </div>
          </Marker>
        );
      })()}

      {/* Info popup */}
      {popup && (
        <Popup
          longitude={popup.lng}
          latitude={popup.lat}
          anchor="bottom"
          offset={16}
          closeButton={false}
          onClose={() => setPopup(null)}
        >
          <div style={popupStyle.wrapper}>
            {popup.utc && (
              <div style={popupStyle.row}>
                <span style={popupStyle.label}>🕐 Hora</span>
                <span style={popupStyle.value}>{formatUtc(popup.utc)}</span>
              </div>
            )}
            <div style={popupStyle.row}>
              <span style={popupStyle.label}>🚚 Estado</span>
              <span style={popupStyle.badge(isMoving(popup.speed))}>
                {isMoving(popup.speed) ? 'En movimiento' : 'Detenido'}
              </span>
            </div>
            <div style={popupStyle.row}>
              <span style={popupStyle.label}>⚡ Velocidad</span>
              <span style={popupStyle.value}>{(popup.speed ?? 0).toFixed(1)} km/h</span>
            </div>
            {popup.battery != null && (
              <div style={popupStyle.row}>
                <span style={popupStyle.label}>🔋 Batería</span>
                <span style={popupStyle.value}>{popup.battery}%</span>
              </div>
            )}
          </div>
        </Popup>
      )}
    </Map>
  );
};

/* keyframe injected once for the live-position pulse */
if (typeof document !== 'undefined' && !document.getElementById('tracking-pulse-kf')) {
  const s = document.createElement('style');
  s.id = 'tracking-pulse-kf';
  s.textContent = `@keyframes tracking-pulse {
    0%   { transform: scale(0.8); opacity: 0.3; }
    70%  { transform: scale(1.7); opacity: 0;   }
    100% { transform: scale(0.8); opacity: 0;   }
  }`;
  document.head.appendChild(s);
}

export default TrackingMap;
